import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { User, AuthState, AuthSession, SessionEndedReason } from './types';

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  session: null,
  endedReason: null,
};

interface LoginPayload {
  user: User;
  session: AuthSession;
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login(state, action: PayloadAction<LoginPayload>) {
      state.user = action.payload.user;
      state.session = action.payload.session;
      state.token = action.payload.session.accessToken;
      state.isAuthenticated = true;
      state.endedReason = null;
      state.lastSessionExpiresAt = null;
    },
    /** Replace tokens after a successful refresh — keeps user/profile intact. */
    refreshSession(state, action: PayloadAction<AuthSession>) {
      state.session = action.payload;
      state.token = action.payload.accessToken;
    },
    /**
     * Forced sign-out. The reason is shown to the user via the
     * SessionEndedBanner so they understand why they were logged out — e.g.
     * after refresh-token reuse-detection trips on the backend.
     *
     * Defaults to `'manual'` when called with no argument so existing
     * `dispatch(logout())` callsites keep working.
     */
    logout: {
      reducer(state, action: PayloadAction<SessionEndedReason>) {
        // Capture the just-cleared session's expiry so the LoginModal
        // can display "Your previous session expired on …".
        state.lastSessionExpiresAt = state.session?.expiresAt ?? null;
        state.user = null;
        state.session = null;
        state.token = undefined;
        state.isAuthenticated = false;
        state.endedReason = action.payload;
      },
      prepare(reason?: SessionEndedReason) {
        return { payload: reason ?? ('manual' as SessionEndedReason) };
      },
    },
    dismissSessionBanner(state) {
      state.endedReason = null;
    },
    updateProfile(state, action: PayloadAction<Partial<User>>) {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
  },
});

export const { login, refreshSession, logout, updateProfile, dismissSessionBanner } = authSlice.actions;
export default authSlice.reducer;
